// SPDX-License-Identifier: GPL-3.0-or-later
/** Main-thread export transport; never imports an engine or retains completed PDF bytes. */
import type { MaterializationRequest } from "./multikey-materializer";
import type { MultikeyExportSnapshot } from "./multikey-export-snapshot";
import type { MultikeyRangeSource, MultikeyWorkerPort } from "./multikey-worker-client";
import type { MultikeyRangeMetadata } from "./multikey-worker-protocol";
import {
  MULTIKEY_MATERIALIZER_PROFILE,
  MULTIKEY_MAX_INPUT_BYTES,
} from "./multikey-worker-protocol";
import {
  MULTIKEY_EXPORT_PROFILE,
  MULTIKEY_EXPORT_PROTOCOL_VERSION,
  MULTIKEY_MAX_EXPORT_BYTES,
} from "./multikey-export-protocol";
import type {
  MultikeyExportWorkerRequest,
  MultikeyExportWorkerReply,
} from "./multikey-export-protocol";

/** Lazy worker factory and client lifetime cancellation. */
export interface MultikeyExportWorkerClientOptions {
  readonly signal?: AbortSignal;
  readonly workerUrl?: string | URL;
  readonly workerFactory?: (url: string | URL, options: WorkerOptions) => MultikeyWorkerPort;
}
const cancelled = (): Error => new DOMException("Multikey export cancelled", "AbortError");

/** One active job and one in-flight command; failure requires a fresh begin. */
export class MultikeyExportWorkerClient {
  private worker?: MultikeyWorkerPort;
  private generation = 0;
  private nextId = 0;
  private disposed = false;
  private active = false;
  private busy = false;
  private pending?: {
    id: number;
    resolve: (reply: MultikeyExportWorkerReply) => void;
    reject: (error: unknown) => void;
  };
  private rejectOperation?: (error: unknown) => void;
  private removeListeners?: () => void;
  private removeJobAbort?: () => void;
  private readonly onAbort = (): void => this.dispose();

  private constructor(private readonly options: MultikeyExportWorkerClientOptions) {
    this.check();
    options.signal?.addEventListener("abort", this.onAbort, { once: true });
  }

  /** Creates a lazy client; no worker or Blob read occurs until begin. */
  public static create(
    options: MultikeyExportWorkerClientOptions = {},
  ): MultikeyExportWorkerClient {
    return new MultikeyExportWorkerClient({ ...options });
  }

  /** Reject revoked client authority before any work or transfer. */
  private check(signal?: AbortSignal, generation = this.generation): void {
    if (this.disposed || this.options.signal?.aborted || signal?.aborted) throw cancelled();
    if (generation !== this.generation) throw cancelled();
  }

  /** Revoke and physically release the worker even when a Blob read is unresolved. */
  private stop(error: unknown): void {
    const worker = this.worker;
    this.worker = undefined;
    this.active = false;
    this.generation++;
    this.removeJobAbort?.();
    this.removeJobAbort = undefined;
    this.removeListeners?.();
    this.removeListeners = undefined;
    const pending = this.pending;
    this.pending = undefined;
    try {
      worker?.terminate();
    } catch {
      /* Still revoke and settle a broken host port. */
    }
    pending?.reject(error);
    this.rejectOperation?.(error);
  }

  /** Runs one bounded operation without a queue; cancellation wins over stalled reads. */
  private operation<T>(signal: AbortSignal | undefined, run: () => Promise<T>): Promise<T> {
    if (this.busy) return Promise.reject(new Error("Concurrent export operation"));
    this.busy = true;
    const abort = (): void => this.stop(cancelled());
    const cancellation = new Promise<never>((_, reject) => {
      this.rejectOperation = reject;
    });
    signal?.addEventListener("abort", abort, { once: true });
    const work = (async () => {
      this.check(signal);
      return run();
    })();
    return Promise.race([work, cancellation])
      .catch(error => {
        this.stop(error);
        throw error;
      })
      .finally(() => {
        signal?.removeEventListener("abort", abort);
        this.rejectOperation = undefined;
        this.busy = false;
      });
  }

  /** Correlates one command to the current physical worker lifetime. */
  private rpc(
    message: MultikeyExportWorkerRequest,
    transfer: Transferable[] = [],
  ): Promise<MultikeyExportWorkerReply> {
    this.check(undefined, message.generation);
    return new Promise((resolve, reject) => {
      this.pending = { id: message.id, resolve, reject };
      try {
        this.worker!.postMessage(message, transfer);
      } catch (error) {
        this.stop(error);
      }
    });
  }

  /** Handshake must succeed before any large Blob is materialized. */
  private async initialize(): Promise<void> {
    const generation = ++this.generation;
    const worker = (this.options.workerFactory ?? ((url, options) => new Worker(url, options)))(
      this.options.workerUrl ?? new URL("./multikey-export-worker.js", import.meta.url),
      { type: "module" },
    );
    this.worker = worker;
    const message = (event: MessageEvent<MultikeyExportWorkerReply>): void => {
      const reply = event.data;
      if (
        worker !== this.worker ||
        generation !== this.generation ||
        !reply ||
        reply.generation !== generation ||
        reply.id !== this.pending?.id
      )
        return;
      const pending = this.pending!;
      this.pending = undefined;
      if (reply.ok) pending.resolve(reply);
      else this.stop(new Error(reply.error));
      if (!reply.ok) pending.reject(new Error(reply.error));
    };
    const error = (): void => {
      if (worker === this.worker) this.stop(new Error("Multikey export worker transport failure"));
    };
    worker.addEventListener("message", message as EventListener);
    worker.addEventListener("error", error);
    worker.addEventListener("messageerror", error);
    this.removeListeners = () => {
      worker.removeEventListener("message", message as EventListener);
      worker.removeEventListener("error", error);
      worker.removeEventListener("messageerror", error);
    };
    const reply = await this.rpc({ type: "init", id: ++this.nextId, generation });
    this.check(undefined, generation);
    if (
      !reply.ok ||
      reply.type !== "init" ||
      reply.protocolVersion !== MULTIKEY_EXPORT_PROTOCOL_VERSION ||
      reply.profile !== MULTIKEY_EXPORT_PROFILE ||
      reply.materializerProfile !== MULTIKEY_MATERIALIZER_PROFILE
    )
      throw new Error("Multikey export worker compatibility mismatch");
  }

  /** Starts one canonical job. The begin signal owns the entire begin-to-finish lifetime. */
  public begin(
    blob: Blob,
    snapshot: MultikeyExportSnapshot,
    options: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const signal = options.signal;
    return this.operation(signal, async () => {
      if (this.active) throw new Error("Export job already active");
      const captured = structuredClone(snapshot);
      if (
        !Number.isSafeInteger(blob.size) ||
        blob.size < 8 ||
        blob.size > MULTIKEY_MAX_EXPORT_BYTES
      )
        throw new Error("Export canonical byte limit");
      const abort = (): void => this.stop(cancelled());
      signal?.addEventListener("abort", abort, { once: true });
      this.removeJobAbort = () => signal?.removeEventListener("abort", abort);
      await this.initialize();
      const generation = this.generation;
      const data = await blob.arrayBuffer();
      this.check(signal, generation);
      if (data.byteLength !== blob.size) throw new Error("Export canonical byte length mismatch");
      const reply = await this.rpc(
        { type: "begin", id: ++this.nextId, generation, data, snapshot: captured },
        [data],
      );
      this.check(signal, generation);
      if (!reply.ok || reply.type !== "begin") throw new Error("Invalid export begin response");
      this.active = true;
    });
  }

  /** Transfers one verified immutable source; metadata and request are cloned at admission. */
  public append(
    source: MultikeyRangeSource,
    request: MaterializationRequest,
    options: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const signal = options.signal;
    return this.operation(signal, async () => {
      if (!this.active) throw new Error("Export append requires begin");
      const { blob, ...metadata } = source;
      const range: MultikeyRangeMetadata = structuredClone(metadata);
      const captured = structuredClone(request);
      if (
        !Number.isSafeInteger(range.byteLength) ||
        range.byteLength < 8 ||
        range.byteLength > MULTIKEY_MAX_INPUT_BYTES ||
        blob.size !== range.byteLength ||
        !/^[a-f0-9]{64}$/.test(range.identity)
      )
        throw new Error("Export range byte length or identity invalid");
      const generation = this.generation;
      const data = await blob.arrayBuffer();
      this.check(signal, generation);
      if (data.byteLength !== range.byteLength)
        throw new Error("Export range byte length mismatch");
      const reply = await this.rpc(
        { type: "append", id: ++this.nextId, generation, data, range, request: captured },
        [data],
      );
      this.check(signal, generation);
      if (!reply.ok || reply.type !== "append") throw new Error("Invalid export append response");
    });
  }

  /** Serializes a bounded result, terminating the physical worker on success or failure. */
  public finish(options: { signal?: AbortSignal } = {}): Promise<Uint8Array> {
    const signal = options.signal;
    return this.operation(signal, async () => {
      if (!this.active) throw new Error("Export finish requires begin");
      const generation = this.generation;
      const reply = await this.rpc({ type: "finish", id: ++this.nextId, generation });
      this.check(signal, generation);
      if (
        !reply.ok ||
        reply.type !== "finish" ||
        !(reply.result instanceof Uint8Array) ||
        reply.result.byteLength < 8 ||
        reply.result.byteLength > MULTIKEY_MAX_EXPORT_BYTES ||
        reply.result.buffer.byteLength > MULTIKEY_MAX_EXPORT_BYTES
      )
        throw new Error("Invalid export result byte limit or response");
      const result = reply.result;
      // Successful cleanup must not reject the operation returning this result.
      this.rejectOperation = undefined;
      this.stop(cancelled());
      return result;
    });
  }

  /** Permanently revokes this client and physically terminates any active/idle worker. */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.options.signal?.removeEventListener("abort", this.onAbort);
    this.stop(cancelled());
  }
}
